import { useMemo } from 'react';
import { useDrillsData } from './domain/useDrillsData';
import { useCrew } from './useCrew';
import { useDefects } from './domain/defects/useDefects';
import { useVessel } from '../contexts/VesselContext';
import { useStorageState } from './useStorageState';
import { useHoursOfRestData } from './domain/useHoursOfRestData';
import { calculateCrewStats } from '../components/tailwind/modules/crew/utils/crewHelpers';
import { calculateDefectStats } from '../components/tailwind/modules/defects/utils/defectHelpers';
import { useNormalizedMaintenanceJobs } from './domain/maintenance/useNormalizedMaintenanceJobs';

/**
 * Dashboard Compliance Hook
 * Calculates real-time compliance percentages from actual vessel data
 *
 * FT-90: Industry-Standard SMS Compliance Scoring
 * ================================================
 * This hook implements the OVERALL SMS COMPLIANCE SCORE displayed on the dashboard.
 * It aggregates compliance metrics from individual modules using weighted averaging.
 *
 * MCA/ISM Code Alignment:
 * - Focuses on CURRENT OPERATIONAL STATE, not historical ratios
 * - "Are we ready for an audit TODAY?" vs "How did we do historically?"
 * - Aligns with UK Workboat Code and ISM Code requirements
 *
 * Component Formulas (see individual module implementations):
 * - Drills (30%): Open Due model - (total - overdue - dueSoon) / total × 100
 * - Equipment/Maintenance (30%): Same Open Due model for jobs
 * - Crew Certificates (30%): On Duty compliance (regulatory focus)
 * - Defects (10%): Penalty model - penalizes for open defects
 *
 * NOT Included in SMS Compliance (standalone operational metrics):
 * - Incidents: SLA-based metric (time-to-close targets)
 * - Risk Assessments: Review Due model (annual review compliance)
 *
 * UPDATED 2026-03-29: FT-90 industry-standard compliance scoring
 * UPDATED 2026-02-18: Now reads maintenance data from JOBS LAYER per Domain Spine v2
 *
 * @returns {Object} Compliance data with percentages and counts
 */
export const useDashboardCompliance = () => {
  // Pull data from domain hooks
  const drillsData = useDrillsData();
  const { crew } = useCrew();
  const defectsData = useDefects();
  const { getCurrentVessel, currentVesselId } = useVessel();
  const currentVessel = getCurrentVessel();
  const vesselId = currentVessel?.id;

  // FT-164: Use new flat array hook
  const { data: horRecords = [] } = useHoursOfRestData();

  // FT-164: Convert flat array to legacy nested lookup for compatibility
  const hoursData = useMemo(() => {
    const nested = {};
    for (const record of horRecords) {
      if (!record.crewId || !record.date) continue;
      if (!nested[record.crewId]) nested[record.crewId] = {};
      nested[record.crewId][record.date] = record;
    }
    return nested;
  }, [horRecords]);
  const { value: familiarisationRecords = [] } = useStorageState('familiarisation_records', []);
  const { value: familiarisationSettings = {} } = useStorageState('familiarisationSettings', {});
  const familiarisationValidityMonths = familiarisationSettings.validityPeriodMonths || 12;
  const drillsKey = currentVesselId ? `drills_${currentVesselId}` : 'drills';
  const { value: drillsStorageData = [] } = useStorageState(drillsKey, []);

  // Use normalized jobs with calculated status (same source as PlannedMaintenanceTab)
  const { stats: maintenanceStats } = useNormalizedMaintenanceJobs();

  const compliance = useMemo(() => {
    // === DRILLS COMPLIANCE ===
    let drillCompliance = 100;
    let overdueDrillsCount = 0;

    try {
      if (drillsData?.getCompliancePercentage) {
        drillCompliance = drillsData.getCompliancePercentage();
      }

      if (drillsData?.getOverdueDrills) {
        const overdueDrills = drillsData.getOverdueDrills();
        overdueDrillsCount = overdueDrills?.length || 0;
      }
    } catch (error) {
      console.warn('[useDashboardCompliance] Error calculating drills compliance:', error);
      drillCompliance = 100;
    }

    // === EQUIPMENT/MAINTENANCE COMPLIANCE ===
    // Stats already calculated by shared hook — single source of truth with PlannedMaintenanceTab
    let equipmentCompliance = 100;
    let overdueJobsCount = 0;

    try {
      overdueJobsCount = maintenanceStats.overdue;
      equipmentCompliance = maintenanceStats.compliance;
    } catch (error) {
      console.warn('[useDashboardCompliance] Error calculating equipment compliance:', error);
      equipmentCompliance = 100;
    }

    // === CREW CERTIFICATES COMPLIANCE ===
    // FT-152: Uses SAME calculateCrewStats() function as CrewSummaryCard
    // Single source of truth - Dashboard matches Crew Module exactly
    let crewCompliance = 100;
    let expiringCertsCount = 0;
    let missingCertsCount = 0;
    let missingFamiliarisationCount = 0;

    try {
      if (crew && Array.isArray(crew) && crew.length > 0) {
        // FT-152: Use calculateCrewStats() - the SAME function CrewSummaryCard uses
        // This ensures Dashboard and Crew Module show identical compliance percentages
        const crewStats = calculateCrewStats(
          crew,
          hoursData,
          currentVessel,
          drillsStorageData,
          familiarisationRecords,
          familiarisationValidityMonths,
          vesselId
        );

        // FT-152: Use SAME formula as CrewSummaryCard line 36-38
        // SMS Compliance = (On Duty crew with no issues / Total On Duty) × 100
        const activeCount = crewStats.active || 0;
        const onDutyCompliant = crewStats.onDutyCompliant ?? activeCount - (crewStats.onDutyIssues || 0);

        crewCompliance = activeCount > 0
          ? Math.round((onDutyCompliant / activeCount) * 100)
          : 100;

        // Extract counts from stats for status messages
        expiringCertsCount = crewStats.warnings || 0;
        missingCertsCount = crewStats.issues || 0;
        // Note: familiarisation issues are included in the overall issues count
      }
    } catch (error) {
      console.warn('[useDashboardCompliance] Error calculating crew compliance:', error);
      crewCompliance = 100;
    }

    // === DEFECTS COMPLIANCE ===
    // FT-180: Uses shared calculateDefectStats() - single source of truth with DefectsModule
    let defectCompliance = 100;
    let openDefectsCount = 0;
    let criticalDefectsCount = 0;

    try {
      const defectsList = defectsData?.defects || [];
      const defectStats = calculateDefectStats(defectsList);

      openDefectsCount = defectStats.active;
      criticalDefectsCount = defectStats.critical;
      defectCompliance = defectStats.compliance;
    } catch (error) {
      console.warn('[useDashboardCompliance] Error calculating defects compliance:', error);
      defectCompliance = 100;
    }

    // === FT-185: REMOVED OVERALL SMS COMPLIANCE ===
    // FT-185: Removed coreAverage, defectPenalty, and overall calculations
    // Now shows 4 independent category indicators instead of one combined score

    return {
      // FT-185: Individual compliance percentages only (no overall score)
      drills: Math.max(0, Math.min(100, drillCompliance)),
      equipment: Math.max(0, Math.min(100, equipmentCompliance)),
      crew: Math.max(0, Math.min(100, crewCompliance)),
      defects: Math.max(0, Math.min(100, defectCompliance)),

      // Counts for status messages
      overdueDrillsCount,
      overdueJobsCount,
      expiringCertsCount,
      missingCertsCount,
      missingFamiliarisationCount,
      openDefectsCount,
      criticalDefectsCount
    };
  }, [drillsData, crew, defectsData, maintenanceStats, hoursData, familiarisationRecords, familiarisationValidityMonths, drillsStorageData, currentVessel, vesselId]);

  return compliance;
};
